import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from 'shared/contexts/AuthContext';
import { getManagerByUserAccountId, createManagerAccountSetup } from 'domains/customers/data/managers';
import { getCustomer } from 'domains/customers/data/customers';
import { updateUserAccountFields } from 'shared/services/users';
import { Manager } from 'domains/customers/model/manager';
import { Customer } from 'domains/customers/model/customer';

interface ManagerContextType {
  manager: Manager | null;
  /** One-shot fetch of the manager's customer doc — not a live listener (see `services/customers.ts`). */
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  /** Re-resolves the manager doc — call after the onboarding wizard completes
   *  so the rest of the area picks up e.g. a flipped `isFirstTime`. */
  refreshManager: () => void;
  /** Re-fetches the customer doc — call after `manager_settings_home` saves company/billing info. */
  refreshCustomer: () => void;
}

const ManagerContext = createContext<ManagerContextType | undefined>(undefined);

export function useManager() {
  const ctx = useContext(ManagerContext);
  if (!ctx) throw new Error('useManager must be used within a ManagerProvider');
  return ctx;
}

/** The current manager when rendered inside a `ManagerProvider`, otherwise `null` (never throws). */
export function useOptionalManager(): Manager | null {
  return useContext(ManagerContext)?.manager ?? null;
}

/**
 * Resolves-or-creates the caller's manager account on every visit to
 * `/manager/*`, ported from aligned's `_handleManagerRedirect`
 * (`auth_guard.dart:53-114`): no role/claim check — any authenticated user
 * gets a `managers`/`customers` doc pair auto-provisioned on first visit.
 * `lastAppType: 'MANAGER'` is stamped on every visit (not just the first),
 * matching `auth_guard.dart:65-66`; `currentCustomerId` is only stamped when
 * a manager account is newly created, matching `auth_guard.dart:82-89`.
 */
export function ManagerProvider({ children }: { children: ReactNode }) {
  const { userAccount } = useAuth();
  const userAccountRef = useRef(userAccount);
  userAccountRef.current = userAccount;
  const [manager, setManager] = useState<Manager | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);
  // Auth account snapshots are recreated for metadata and unrelated field
  // updates. Manager resolution only depends on these identity keys; using
  // the whole account object here would loop with the lastAppType write below.
  const userAccountId = userAccount?.id ?? null;
  const currentCustomerId = userAccount?.currentCustomerId ?? null;

  useEffect(() => {
    let mounted = true;
    const account = userAccountRef.current;
    if (!account) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        await updateUserAccountFields(account.id, { lastAppType: 'MANAGER' });

        let mgr = await getManagerByUserAccountId(account.id, currentCustomerId);
        if (!mgr) {
          mgr = await createManagerAccountSetup(account);
          await updateUserAccountFields(account.id, { currentCustomerId: mgr.customerId });
        }
        if (!mounted) return;
        setManager(mgr);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to set up your manager account.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();

    return () => {
      mounted = false;
    };
  }, [currentCustomerId, refreshKey, userAccountId]);

  useEffect(() => {
    let mounted = true;
    if (!manager) {
      setCustomer(null);
      return;
    }
    getCustomer(manager.customerId).then((c) => {
      if (mounted) setCustomer(c);
    });
    return () => {
      mounted = false;
    };
  }, [manager, customerRefreshKey]);

  const refreshManager = useCallback(() => setRefreshKey((k) => k + 1), []);
  const refreshCustomer = useCallback(() => setCustomerRefreshKey((k) => k + 1), []);

  return (
    <ManagerContext.Provider value={{ manager, customer, loading, error, refreshManager, refreshCustomer }}>
      {children}
    </ManagerContext.Provider>
  );
}
